import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/apiBase";
import { useBookingWindow } from "@/lib/useBookingWindow";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Return the Sunday of the week containing `d`. */
const sundayOfWeek = (d) => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay()); // Sunday = 0
  return out;
};

/**
 * Renders a Sunday–Saturday week strip. The strip shows the week containing
 * `anchorDate` by default and offers prev/next week navigation bounded to
 * the same 30-day booking window the calendar uses. The internal
 * `viewWeekStart` state lets the user browse other weeks without changing
 * their actively selected booking date — clicking a tile still calls
 * `onDateSelect(date)`. Data pulled from /api/calendar/week-summary.
 */
const NextSevenDaysStrip = ({
  anchorDate,
  onDateSelect,
  courtId = null,
  label = "Weekly Availability",
  className = "",
  maxDaysAhead,
}) => {
  // Falls back to the centrally-configured booking window when the caller
  // doesn't override it — keeps the strip's prev/next bounds in sync with
  // whatever the admin has currently published (30 + extra_days).
  const { windowDays: configuredWindow } = useBookingWindow();
  const effectiveMaxDays = maxDaysAhead ?? configuredWindow;
  const [days, setDays] = useState([]);
  const [viewWeekStart, setViewWeekStart] = useState(() =>
    sundayOfWeek(anchorDate || new Date())
  );

  // Pre-compute the earliest/latest week we'll allow the user to browse.
  // Anchored to the Sunday of `today` and the Sunday of `today + N days` so
  // the strip mirrors the calendar's valid date range exactly.
  const today = (() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  })();
  const minWeekStart = sundayOfWeek(today);
  const maxWeekStart = (() => {
    const d = new Date(today);
    d.setDate(d.getDate() + effectiveMaxDays);
    return sundayOfWeek(d);
  })();

  const canPrev = viewWeekStart > minWeekStart;
  const canNext = viewWeekStart < maxWeekStart;

  // When the user picks a new anchor date that lives outside the currently
  // viewed week, snap the strip back to that week.
  useEffect(() => {
    if (!anchorDate) return;
    const anchorWeek = sundayOfWeek(anchorDate);
    if (toDateStr(anchorWeek) !== toDateStr(viewWeekStart)) {
      setViewWeekStart(anchorWeek);
    }
    // intentionally only react to anchorDate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorDate]);

  const load = useCallback(async () => {
    if (!viewWeekStart) return;
    const q = new URLSearchParams({
      start: toDateStr(viewWeekStart),
      days: "7",
    });
    if (courtId) q.set("court_id", courtId);
    try {
      const res = await axios.get(`${API}/calendar/week-summary?${q.toString()}`);
      setDays(
        (res.data.days || []).map((d) => ({
          date: new Date(d.date + "T00:00:00"),
          free: d.free,
          total: d.total,
        }))
      );
    } catch {
      setDays([]);
    }
  }, [viewWeekStart, courtId]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftWeek = (deltaDays) => {
    setViewWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + deltaDays);
      // Clamp to the valid 30-day window so we never step out of bounds.
      if (next < minWeekStart) return minWeekStart;
      if (next > maxWeekStart) return maxWeekStart;
      return next;
    });
  };

  // Don't bail when days.length === 0 mid-shift; keep strip mounted so the
  // arrows remain usable. Empty week renders 7 placeholder buttons via the
  // backend response or — if the API failed — nothing.
  const weekRangeLabel = (() => {
    if (days.length === 0) return "";
    const first = days[0].date;
    const last = days[days.length - 1].date;
    const sameMonth = first.getMonth() === last.getMonth();
    const fmt = (d, opts) => d.toLocaleDateString("en-US", opts);
    const a = fmt(first, { month: "short", day: "numeric" });
    const b = sameMonth
      ? fmt(last, { day: "numeric" })
      : fmt(last, { month: "short", day: "numeric" });
    return `${a} – ${b}`;
  })();

  return (
    <div className={`card-surface p-3 ${className}`}>
      <div className="flex items-center justify-between mb-2 px-1 gap-2">
        <p className="label-text">{label}</p>
        <div className="flex items-center gap-1.5">
          {weekRangeLabel && (
            <span
              data-testid="week-range-label"
              className="text-[10px] uppercase tracking-wider text-[#a1a1aa] mr-1"
            >
              {weekRangeLabel}
            </span>
          )}
          <button
            type="button"
            data-testid="week-prev-btn"
            onClick={() => shiftWeek(-7)}
            disabled={!canPrev}
            aria-label="Previous week"
            className="p-1 rounded-sm border border-white/15 text-[#a1a1aa] hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#a1a1aa] disabled:hover:border-white/15"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            data-testid="week-next-btn"
            onClick={() => shiftWeek(7)}
            disabled={!canNext}
            aria-label="Next week"
            className="p-1 rounded-sm border border-white/15 text-[#a1a1aa] hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#a1a1aa] disabled:hover:border-white/15"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map(({ date, free, total }) => {
          const pct = total > 0 ? Math.round((free / total) * 100) : 0;
          // Clone to drop time component for apples-to-apples day compare.
          const d0 = new Date(date);
          d0.setHours(0, 0, 0, 0);
          const maxDate = new Date(today);
          // Match the backend: exactly `effectiveMaxDays` dates starting
          // from today, so today+0 through today+(N-1) inclusive.
          maxDate.setDate(maxDate.getDate() + Math.max(0, effectiveMaxDays - 1));
          maxDate.setHours(0, 0, 0, 0);
          const isOutOfWindow = d0 < today || d0 > maxDate;
          // Hide out-of-window dates entirely — render an empty cell that
          // preserves the 7-column grid but shows nothing (no tile, no
          // border, no text). The user asked for anything beyond 30 days
          // to disappear from the public availability strip.
          if (isOutOfWindow) {
            return <div key={toDateStr(date)} aria-hidden />;
          }
          const intensity =
            pct >= 75
              ? "bg-[#8cc63f]/90 text-black border-[#8cc63f]"
              : pct >= 40
              ? "bg-yellow-400/30 text-yellow-100 border-yellow-400/50"
              : pct > 0
              ? "bg-orange-500/30 text-orange-200 border-orange-500/50"
              : "bg-red-500/30 text-red-200 border-red-500/50";
          const isSelected =
            anchorDate &&
            toDateStr(date) === toDateStr(anchorDate);
          return (
            <button
              key={toDateStr(date)}
              data-testid={`week-day-${toDateStr(date)}`}
              type="button"
              onClick={() =>
                onDateSelect && onDateSelect(new Date(date))
              }
              className={`flex flex-col items-center py-2 px-1 rounded-sm border text-center transition-transform ${intensity} ${
                isSelected ? "ring-2 ring-white/80" : ""
              } hover:scale-[1.03]`}
            >
              <span className="text-[9px] uppercase tracking-wider opacity-80">
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </span>
              <span className="text-base font-black leading-tight">
                {date.getDate()}
              </span>
              <span className="text-[9px] font-bold opacity-90">
                {free} available
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NextSevenDaysStrip;
